import { useTranslation } from '../../shared/i18n';
import React from 'react';
import { Alert, List, Typography, Tag, Button, Space } from 'antd';
import {
    ExclamationCircleOutlined,
    UserOutlined,
    FormOutlined,
    SwapOutlined,
    FileTextOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

import { userPath } from '../../shared/config/paths';

const { Text, Link } = Typography;

/**
 * Validation Error Alert Component
 *
 * Displays validation errors with actionable suggestions.
 * Supports different fix_location types to guide users.
 *
 * Error format expected:
 * {
 *   field: 'additional.birth_date',
 *   code: 'required',
 *   message: '.FR individual registrations require Birth Date',
 *   suggestion: 'Please fill in Birth Date...',
 *   fix_location: 'contact_extension' // contact_basic | contact_extension | contact_selection | domain_additional
 * }
 */
const ValidationErrorAlert = ({
    errors = [],
    onFixClick,
    contactId,
    style
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    if (!errors || errors.length === 0) return null;

    // Group errors by fix_location
    const groupedErrors = errors.reduce((acc, error) => {
        const location = error.fix_location || 'other';
        if (!acc[location]) acc[location] = [];
        acc[location].push(error);
        return acc;
    }, {});

    // Get icon and label for fix_location
    const getLocationInfo = (location) => {
        switch (location) {
            case 'contact_basic':
                return {
                    icon: <UserOutlined />,
                    label: t('Basic contact information'),
                    color: 'blue',
                    action: t('Edit contact')
                };
            case 'contact_extension':
                return {
                    icon: <FormOutlined />,
                    label: t('Contact TLD extension fields'),
                    color: 'orange',
                    action: t('Edit extended information')
                };
            case 'contact_selection':
                return {
                    icon: <SwapOutlined />,
                    label: t('Select contact'),
                    color: 'purple',
                    action: t('Select another contact')
                };
            case 'domain_additional':
                return {
                    icon: <FileTextOutlined />,
                    label: t('Additional domain information'),
                    color: 'green',
                    action: t('Enter additional information')
                };
            default:
                return {
                    icon: <ExclamationCircleOutlined />,
                    label: t('Other'),
                    color: 'default',
                    action: null
                };
        }
    };

    // Handle fix button click
    const handleFixClick = (location, errors) => {
        if (onFixClick) {
            onFixClick(location, errors);
            return;
        }

        // Default navigation behavior
        switch (location) {
            case 'contact_basic':
            case 'contact_extension':
                navigate(userPath('/domain-registration/contact-templates'));
                break;
            case 'contact_selection':
                // Usually handled in the modal, no navigation needed
                break;
            default:
                break;
        }
    };

    return (
        <Alert
            type="error"
            showIcon
            icon={<ExclamationCircleOutlined />}
            message={t("Domain registration validation failed")}
            description={
                <div style={{ marginTop: 8 }}>
                    {Object.entries(groupedErrors).map(([location, locationErrors]) => {
                        const info = getLocationInfo(location);
                        return (
                            <div key={location} style={{ marginBottom: 16 }}>
                                <Space style={{ marginBottom: 8 }}>
                                    <Tag icon={info.icon} color={info.color}>
                                        {info.label}
                                    </Tag>
                                    {info.action && (
                                        <Button
                                            type="link"
                                            size="small"
                                            onClick={() => handleFixClick(location, locationErrors)}
                                        >
                                            {info.action}
                                        </Button>
                                    )}
                                </Space>
                                <List
                                    size="small"
                                    dataSource={locationErrors}
                                    renderItem={(error) => (
                                        <List.Item style={{ padding: '4px 0', border: 'none' }}>
                                            <div>
                                                <Text type="danger">
                                                    <ExclamationCircleOutlined style={{ marginRight: 8 }} />
                                                    {error.message}
                                                </Text>
                                                {error.suggestion && (
                                                    <div style={{ marginLeft: 22, marginTop: 4 }}>
                                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                                            💡 {error.suggestion}
                                                        </Text>
                                                    </div>
                                                )}
                                            </div>
                                        </List.Item>
                                    )}
                                />
                            </div>
                        );
                    })}
                </div>
            }
            style={style}
        />
    );
};

export default ValidationErrorAlert;
